/**
 * Frontend compendium wiring.
 *
 * The bundled base packs (`@massif/lancer-data` + `@massif/long-rim-data`) are
 * plain npm data, so the core compendium builds synchronously — no fetch. On top
 * of that we layer any `.lcp` packs the user previously persisted to IndexedDB,
 * which requires an async rebuild. `useCompendium` exposes both: the synchronous
 * base compendium immediately, then swaps in the IDB-merged one once it loads.
 */
import { useEffect, useState } from "react";
import {
  Compendium,
  loadCoreCompendium,
  loadPackFromZip,
  rebuildCompendiumFromStore,
  type Pack,
} from "@lancer-vtt/compendium";
import {
  SEED_OVERLAY,
  parseSidecar,
  loadSidecarChain,
  mergeOverlays,
  type LoadedOverlay,
  type ParsedSidecar,
} from "@lancer-vtt/rules";
import { packStore } from "./packStore";
import { fetchWorldPacks, fetchWorldSidecars } from "./api";
import { errorMessage } from "@lancer-vtt/shared";

let baseCompendium: Compendium | undefined;

/** The core compendium (both base packs merged), built once and memoized. */
export function getBaseCompendium(): Compendium {
  if (!baseCompendium) baseCompendium = loadCoreCompendium();
  return baseCompendium;
}

export type CompendiumState = {
  compendium: Compendium;
  /** True until the IndexedDB-persisted packs and the server's world packs have merged in. */
  loadingPersisted: boolean;
  /** Stored/world packs that failed to re-parse on load (surfaced, not swallowed). */
  errors: { id: string; message: string }[];
  /**
   * The merged **action overlay**: the engine's intrinsic `SEED_OVERLAY` merged with every
   * world sidecar pack the server validated at boot, via the SAME
   * `parseSidecar`/`loadSidecarChain`/`mergeOverlays` pipeline `WorldRooms.boot` runs
   * server-side (`backend/src/world/rooms.ts`). **Derive a granted action row's `resolvable`
   * against this** (`enumerateActions({ ..., overlay })`), never the bare `SEED_OVERLAY`
   * default: that default carries no authored deployable bodies, so an authored deployable's
   * bar row renders as an un-fireable "GM adjudicates" advisory. Starts as bare
   * `SEED_OVERLAY` and is replaced once the sidecar fetch settles; see {@link loadingOverlay}.
   */
  overlay: LoadedOverlay;
  /**
   * True until the world-sidecar fetch + overlay merge has settled. Mirrors `loadingPersisted`'s
   * lifecycle (both resolve in the same effect) but kept a separate, explicitly-named flag so a
   * consumer that cares ONLY about overlay readiness (e.g. a granted-action row deciding whether
   * to render at all) doesn't have to reason about what else `loadingPersisted` bundles in.
   */
  loadingOverlay: boolean;
  /**
   * Why the world-sidecar overlay failed to load (the fetch failed, or a sidecar wouldn't parse),
   * or `null`. The overlay then lacks that content, so a mech spawned against it freezes none of
   * its limited-use caps: `mechDeployHoldReason` keeps mech deploys held and names this.
   */
  overlayError: string | null;
}

/**
 * Returns the base compendium immediately, then rebuilds it with the user's own
 * IndexedDB-persisted `.lcp` packs **and** the GM's server-seeded world packs
 * merged on top (later-pack-wins, in that order). A pack that fails to parse is
 * reported in `errors` rather than failing silently.
 *
 * **The world packs are merged transiently — never persisted to IndexedDB.**
 * `PackStore`/IndexedDB stays reserved for the user's *own* custom packs; the GM's
 * (possibly paid) content arrives over the wire on each session and lands only in this
 * live, in-memory Compendium. A GM pack update therefore propagates on the next load
 * with no stale per-browser copies, and the paid content never enters players'
 * persistent storage.
 */
export function useCompendium(): CompendiumState {
  const [compendium, setCompendium] = useState<Compendium>(() => getBaseCompendium());
  const [loadingPersisted, setLoadingPersisted] = useState(true);
  const [errors, setErrors] = useState<{ id: string; message: string }[]>([]);
  const [overlay, setOverlay] = useState<LoadedOverlay>(SEED_OVERLAY);
  const [loadingOverlay, setLoadingOverlay] = useState(true);
  const [overlayError, setOverlayError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const errs: { id: string; message: string }[] = [];
    // The sidecar failures alone (`errs` also collects pack failures), for `overlayError`.
    const overlayErrs: string[] = [];

    // 1) base + the user's own IndexedDB-persisted custom packs.
    const idbMerged = rebuildCompendiumFromStore(packStore, (stored, err) => {
      errs.push({ id: stored.id, message: errorMessage(err) });
    }).catch((err) => {
      // IndexedDB unavailable (e.g. private mode) — fall back to the base packs.
      errs.push({ id: "<store>", message: String(err) });
      return getBaseCompendium();
    });

    // 2) the GM's server-seeded world packs (transient — merged into the live
    //    compendium, NEVER store.put). A server with no world folder returns [].
    const worldPacks = fetchWorldPacks().catch((err) => {
      errs.push({ id: "<world>", message: String(err) });
      return [] as { name: string; bytes: Uint8Array }[];
    });

    // 3) the world's sidecar packs, merged into the SAME action overlay `WorldRooms.boot`
    //    builds server-side, so a granted row's `resolvable` classifies the same way here as
    //    it will on `request_activate`. A pack-level parse failure (bad manifest, a stray
    //    file, …) is reported and that one sidecar dropped (mirrors the server's per-sidecar
    //    skip in `rooms.ts` boot); a total fetch failure (no server, no world folder) degrades
    //    to the bare `SEED_OVERLAY` — matching the world-packs fetch's degrade-not-crash posture.
    //
    //    A per-ENTRY schema rejection (`ParsedSidecar.rejected`) does NOT drop the sidecar — it's
    //    logged per entry below and its siblings still merge in, exactly like the server. This
    //    tier reaches the identical verdict as the server BY CONSTRUCTION: both run the same
    //    `parseSidecar` over the same bytes (`WorldRooms.sidecarFiles()` serves them unmodified,
    //    rejected entries included — see its doc comment), so `entries`/`rejected` here are the
    //    same sets the server computed.
    //
    //    D8 (`decisions/0135`'s 2026-09-03 amendment) — the browser deliberately does NOT run
    //    the CONTENT gate (`partitionSidecarContent`/`requireSidecarContent`, `@lancer-vtt/rules`).
    //    The server can skip an unresolvable entry (an id no loaded pack provides — a purchased
    //    NPC `.lcp` the browser's own compendium build may also lack) and still serve that
    //    entry's raw bytes unmodified (same reason it serves a schema-rejected entry's bytes: a
    //    stripped file would trip THIS module's own manifest-integrity cross-check inside
    //    `parseSidecar`). So the merged overlay here can carry entries the server's own overlay
    //    does not — `sidecarLoadReport`'s `unresolvable` bucket names exactly which. This is
    //    INERT, not a gap to close: an unresolvable id is item-shaped with no compendium item
    //    behind it (or a talent-rank whose base talent isn't loaded), and nothing in the engine
    //    can spawn or equip an entity the compendium lacks — unlike a schema-rejected entry
    //    (active content with lying types), an unreachable one can never fire. `fetchWorldSidecars`
    //    (`./api.ts`) reflects the same posture: it maps `name`+`files` only and ignores the
    //    additive `load` report field entirely — that surface is for `lvtt world sidecars` and
    //    the server-side proofs, not this hook. Revisit only if a future carrier vector ever
    //    resolves an overlay id without a compendium record behind it (`decisions/0135`'s Risk).
    //
    //    HONEST LIMIT: `sidecarOverlayParity.test.ts` does NOT drive this sequence — it
    //    re-implements a DUPLICATE of it in a local test helper (this logic lives inside a React
    //    hook and isn't extractable without a refactor), then diffs that duplicate's output
    //    against a real `WorldRooms.overlayForTest()`, so it cannot notice this sequence drifting
    //    out from under it. What it does prove genuinely: `sidecarFiles()` serves a rejected
    //    entry's raw bytes unmodified. The real `useCompendium` hook, running exactly the code
    //    below, is exercised only by `frontend/e2e/deployables.e2e.ts` — that's where a regression
    //    in THIS sequence would be caught.
    const sidecarOverlay = fetchWorldSidecars()
      .then((sidecars) => {
        const parsed: ParsedSidecar[] = [];
        for (const sc of sidecars) {
          try {
            const oneSidecar = parseSidecar(sc.files);
            for (const rejection of oneSidecar.rejected) {
              console.error(
                `lancer-vtt: world sidecar "${sc.name}" entry "${rejection.id}" failed the schema gate and was skipped: ${rejection.reason}`,
              );
            }
            parsed.push(oneSidecar);
          } catch (err) {
            errs.push({ id: sc.name, message: errorMessage(err) });
            overlayErrs.push(`sidecar "${sc.name}": ${errorMessage(err)}`);
          }
        }
        return mergeOverlays(SEED_OVERLAY, loadSidecarChain(parsed));
      })
      .catch((err) => {
        errs.push({ id: "<sidecars>", message: errorMessage(err) });
        overlayErrs.push(`the sidecar fetch failed: ${errorMessage(err)}`);
        return SEED_OVERLAY;
      });

    Promise.all([idbMerged, worldPacks, sidecarOverlay])
      .then(([idbCompendium, packs, mergedOverlay]) => {
        if (cancelled) return;
        const extra: Pack[] = [];
        for (const p of packs) {
          try {
            // Parse + merge into the LIVE compendium only — no packStore.put.
            extra.push(loadPackFromZip(p.bytes));
          } catch (err) {
            errs.push({ id: p.name, message: errorMessage(err) });
          }
        }
        // Rebuild with base + IDB customs + world packs, preserving load order
        // (later-pack-wins). The world packs ride last so a GM override wins.
        const merged = extra.length
          ? new Compendium([...idbCompendium.packs, ...extra])
          : idbCompendium;
        // Identity-stability guard: when nothing actually merged on top of the base
        // (no IDB customs, no world packs — the common case), keep the *existing*
        // base reference instead of swapping in a fresh-but-equal Compendium. A
        // needless identity churn tears down every downstream consumer keyed on the
        // compendium reference (e.g. `useRemoteRoster`'s connection factory
        // `useMemo([compendium, …])`, which would reconnect the socket). Only swap
        // when the content actually differs.
        const sameAsBase = extra.length === 0 && merged.packs.length === getBaseCompendium().packs.length;
        if (!sameAsBase) setCompendium(merged);
        setOverlay(mergedOverlay);
        setOverlayError(overlayErrs.length > 0 ? overlayErrs.join("; ") : null);
        setErrors(errs);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPersisted(false);
          setLoadingOverlay(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { compendium, loadingPersisted, errors, overlay, loadingOverlay, overlayError };
}
